import { DBSCAN } from 'density-clustering';
import { Camera, Frustum, Matrix4, Vector3 } from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

import { isDefined } from '../../../../../../../../utils/variables';
import { AssetManager } from '../../../../../../../../viewer/asset-manager';
import { Cluster } from '../../../../../../../../viewer/elements/cluster';
import { Poi } from '../../../../../../../../viewer/elements/poi/poi';
import { createLabel, removeLabel } from '../../label-factory';

type ClusterEntry = {
  members: string[];
  label: CSS2DObject;
  cluster: Cluster;
};

export class PoiLabelManager {
  private static _instance = new PoiLabelManager();
  private constructor() {
    /** */
  }
  static get instance() {
    return PoiLabelManager._instance;
  }

  private calculating: boolean = false;

  private pois = new Map<string, Poi>();
  private clusterEntries: ClusterEntry[] = [];

  cleanup() {
    this.clusterEntries = [];
    this.pois.clear();
  }

  register(id: string, poi: Poi) {
    this.pois.set(id, poi);
  }

  unregister(id: string) {
    this.pois.delete(id);
  }

  /**
   * Recomputes POI clusters based on their screen-space proximity using the DBSCAN algorithm.
   *
   * This method:
   * 1. Projects all visible POIs into screen space.
   * 2. Uses DBSCAN to group POIs that are within a defined screen-space distance.
   * 3. Reuses existing cluster objects where possible to avoid unnecessary re-creation.
   * 4. Computes the average 3D position for each cluster and updates its position in the scene.
   * 5. Cleans up any stale clusters that are no longer valid.
   * 6. Toggles visibility of individual POIs based on whether they are currently clustered.
   *
   * Clustering is applied dynamically and should be triggered on camera movement or zoom level changes
   * to maintain spatial grouping and avoid visual clutter.
   */
  recomputeClusters(camera: Camera) {
    if (this.calculating) {
      return;
    }

    this.calculating = true;
    const points: { id: string; x: number; y: number }[] = [];
    const viewMatrix = new Matrix4().multiplyMatrices(
      camera.projectionMatrix,
      camera.matrixWorldInverse,
    );
    const frustum = new Frustum().setFromProjectionMatrix(viewMatrix);

    const clusters = this.resolveClusters(frustum, camera, points);

    const idsInFrustum = new Set(points.map((p) => p.id));
    this.pois.forEach((poi, id) => {
      poi.culled = !idsInFrustum.has(id);
    });

    // Build new list, reusing old entries when members match.
    const newEntries: ClusterEntry[] = [];
    for (const group of clusters) {
      const ids = group.map((i) => points[i].id).sort();
      // Find existing entry with identical members.
      let entry = this.clusterEntries.find(
        (e) => e.members.length === ids.length && e.members.every((u, i) => u === ids[i]),
      );

      if (!entry) {
        const displayPoiId = ids.find((id) => this.pois.get(id)?.getThumbnailResourceUrl('64x_'));

        const cluster = new Cluster({
          size: ids.length,
          displayPoi: displayPoiId ? this.pois.get(displayPoiId) : undefined,
        });
        const css2d = createLabel({ type: 'cluster', cluster: cluster });
        cluster.add(css2d);

        AssetManager.instance.add(cluster);

        entry = { members: ids, label: css2d, cluster: cluster };
      }

      entry.cluster.members = ids.map((id) => this.pois.get(id)!);

      newEntries.push(entry);
    }

    // Tear down any clusters that didn’t survive.
    for (const old of this.clusterEntries) {
      if (!newEntries.includes(old)) {
        // Unmount & remove the React label.
        removeLabel({ type: 'cluster', cluster: old.cluster });

        // Clear out any children
        old.cluster.children.slice().forEach((ch) => old.cluster.remove(ch));

        // Dispose geometries/materials.
        old.cluster.dispose();
        if (isDefined(old.cluster.parent)) {
          old.cluster.parent.remove(old.cluster);
        }
      }
    }

    this.clusterEntries = newEntries;

    const clustered = new Set(newEntries.flatMap((e) => e.members));
    this.pois.forEach((poi, id) => {
      if (!poi.culled) {
        poi.merged = clustered.has(id);
      } else {
        poi.merged = false;
      }
    });

    this.calculating = false;
  }

  /**
   *  Filter out POI's outside of the camera frustum,
   *  and then group the remaining POI's into cluster groups using DBSCAN.
   */
  private resolveClusters(
    frustum: Frustum,
    camera: Camera,
    points: { id: string; x: number; y: number }[],
  ) {
    const filteredPois = [...this.pois].filter(
      (entry) => entry[1].visible && frustum.containsPoint(entry[1].position),
    );

    filteredPois.forEach(([id, poi]) => {
      const worldPosition = poi.position;
      const screenPoint = new Vector3().copy(worldPosition).project(camera);
      points.push({
        id,
        x: (screenPoint.x * 0.5 + 0.5) * window.innerWidth,
        y: (-screenPoint.y * 0.5 + 0.5) * window.innerHeight,
      });
    });

    /** https://en.wikipedia.org/wiki/DBSCAN */
    const dbScan = new DBSCAN();
    const clusters = dbScan.run(
      points.map((p) => [p.x, p.y]),
      50,
      2,
    );
    return clusters;
  }
}
